import client from "@irati/utils/services/apollo"
import sagaErrorHandler from "@irati/deals/services/errors/sagaErrorHandler"
import {Deal, DealSaga, User, DealStockCounts, MutationPerksSagaStartArgs, DealsListType} from "@irati/types"
import {PerksSagaOrderInput} from "@irati/types/ops"
import PBService from "@irati/utils/services"

import fullSagaQuery, {withStockQuery as fullSagaWithStockQuery} from "./queries/GetSaga"
import fullPerksSaga from "./queries/fragments/fullPerksSaga"
import fullSagaStartMutation from "./mutations/SagaStart"
import fullSagaOrderMutation from "./mutations/SagaOrder"
import fullSagaUpdateMutation from "./mutations/SagaUpdate"
import fullSagaPayMutation from "./mutations/SagaPay"
import fullSagaCompleteMutation from "./mutations/SagaComplete"
import stockCount from "./queries/StockQuery"

export interface PerkSagaOrderProduct {
  /** Product Id */
  uuid: string
  quantity: number
  /** value in cents */
  openValue: number
}

interface PerkSagasStockCounts extends Omit<DealStockCounts, "stockTotals"> {
  stockTotals: {[key: string]: number}
}

class PerksSagaService extends PBService {
  async get(uuid: string, {forceRefetch = false, stockCountForDealId = ""} = {}): Promise<DealSaga> {
    if (!forceRefetch && !stockCountForDealId) {
      const fragmentName = fullPerksSaga.definitions[0].name.value
      const cachedSaga = client.readFragment({
        id: `DealSaga@${uuid}`,
        fragment: fullPerksSaga,
        fragmentName
      })
      if (cachedSaga) {
        return cachedSaga
      }
    }

    const fetchPolicy = forceRefetch ? "network-only" : "cache-first"
    const variables = {uuid, dealId: stockCountForDealId}

    const query = stockCountForDealId ? fullSagaWithStockQuery : fullSagaQuery

    return client.query({query, variables, fetchPolicy}).then(({data}) => data.perksSaga)
  }

  stockCount(dealId: string, {forceRefetch = false} = {}): Promise<PerkSagasStockCounts> {
    const fetchPolicy = forceRefetch ? "network-only" : "cache-first"
    const variables = {dealId}

    return client.query({query: stockCount, variables, fetchPolicy}).then(({data}) => {
      const keyedStock: {[key: string]: number} = {}
      data.dealStockCount.stockTotals.forEach((value: any) => {
        keyedStock[value.id] = value.count
      })
      return {...data.dealStockCount, stockTotals: keyedStock}
    })
  }

  start(deal: Deal, user: User): Promise<DealSaga> {
    const variables: MutationPerksSagaStartArgs = {
      details: {
        dealId: deal.uuid,
        userId: user.uuid,
        isCompanyBenefit: deal.type === DealsListType.Company
      }
    }

    const sagaRequest = client.mutate({mutation: fullSagaStartMutation, variables}).then(({data}) => {
      return data.perksSagaStart
    })

    // we always want stock counts when we start a saga
    const stockRequest = this.stockCount(deal.uuid)

    // Can't mix queries and mutations so must parallalise start saga + stock request
    return Promise.all([sagaRequest, stockRequest]).then((res) => res[0])
  }

  order(saga: DealSaga, details: PerksSagaOrderInput): Promise<DealSaga> {
    const variables = {
      sagaId: saga.uuid,
      details
    }

    return client.mutate({mutation: fullSagaOrderMutation, variables}).then(({data}) => data.perksSagaOrder)
  }

  update(saga: DealSaga, details: any): Promise<DealSaga> {
    const variables = {
      sagaId: saga.uuid,
      details
    }

    return client.mutate({mutation: fullSagaUpdateMutation, variables}).then(({data}) => data.perksSagaUpdate)
  }

  pay(saga: DealSaga, payment: any): Promise<DealSaga> {
    const variables = {
      sagaId: saga.uuid,
      details: payment
    }

    return client.mutate({mutation: fullSagaPayMutation, variables}).then(({data}) => data.perksSagaPay)
  }

  complete(saga: DealSaga): Promise<DealSaga> {
    const variables = {
      sagaId: saga.uuid
    }

    return client.mutate({mutation: fullSagaCompleteMutation, variables}).then(({data}) => data.perksSagaComplete)
  }
}

const perksSagaService = new PerksSagaService({errorHandlers: [sagaErrorHandler]})

export default perksSagaService
